import React, { useState } from 'react';
import { Loader2, Trash2, Tag, UserCheck, AlertTriangle, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';

const money = (n) => `£${(Number(n) || 0).toFixed(2)}`;
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB') : '—');

function Side({ label, order, tone }) {
  return (
    <div className={`border p-3 ${tone}`}>
      <div className="text-[10px] font-bold tracking-widest text-white/40">{label}</div>
      <div className="text-white text-sm font-medium mt-1">{order.customer_name || '—'}</div>
      <div className="text-xs text-white/50 mt-0.5">
        {money(order.amount)} · {day(order.date)} · {order.source || 'unknown source'}
      </div>
      <div className="text-[11px] text-white/40 mt-1">
        {order.sage_invoice_number ? `Sage ref ${order.sage_invoice_number} · ` : ''}
        {order.posted ? 'posted to Sage' : 'not posted to Sage'}
      </div>
    </div>
  );
}

// One finding from the audit, with the correcting action inline. A record that
// has already been posted to Sage is never offered an action — its figures are
// on the ledger and have to be corrected in Sage itself.
export default function EbayFindingCard({ finding, busy, onRepair }) {
  const { ebay, other, type } = finding;
  const isDuplicate = type === 'duplicate';
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(other.customer_name || '');
  const [email, setEmail] = useState(other.customer_email || '');

  const working = (action, id) => busy === `${id}${action}`;

  return (
    <div className="border border-white/10 bg-[#111115] p-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`text-[10px] font-bold tracking-widest px-2 py-0.5 border ${
            isDuplicate
              ? 'text-red-400 border-red-400/40 bg-red-400/5'
              : 'text-amber-400 border-amber-400/40 bg-amber-400/5'
          }`}
        >
          {isDuplicate ? 'DUPLICATE' : 'CHECK THIS ONE'}
        </span>
        <span className="text-xs text-white/50">{finding.reason}</span>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <Side label="eBay record" order={ebay} tone="border-white/10" />
        <Side label="Other record" order={other} tone="border-amber-400/20" />
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        {!other.posted && (
          <Button
            size="sm"
            variant="outline"
            className="bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10"
            disabled={working('remove_duplicate', other.id)}
            onClick={() => onRepair('remove_duplicate', other.id)}
          >
            {working('remove_duplicate', other.id) ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Trash2 className="w-3.5 h-3.5" />
            )}
            Remove unposted duplicate
          </Button>
        )}
        {!ebay.posted && (
          <Button
            size="sm"
            variant="outline"
            className="bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10"
            disabled={working('remove_duplicate', ebay.id)}
            onClick={() => onRepair('remove_duplicate', ebay.id)}
          >
            {working('remove_duplicate', ebay.id) ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Trash2 className="w-3.5 h-3.5" />
            )}
            Remove unposted eBay copy
          </Button>
        )}
        {!other.posted && other.source !== 'eBay' && (
          <Button
            size="sm"
            variant="outline"
            className="bg-transparent border-primary/30 text-primary hover:bg-primary/10"
            disabled={working('retag', other.id)}
            onClick={() => onRepair('retag', other.id)}
          >
            {working('retag', other.id) ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Tag className="w-3.5 h-3.5" />
            )}
            File as eBay sale
          </Button>
        )}
        {!editing && (
          <Button
            size="sm"
            variant="outline"
            className="bg-transparent border-white/15 text-white/70 hover:bg-white/5"
            onClick={() => setEditing(true)}
          >
            <UserCheck className="w-3.5 h-3.5" /> Correct buyer details
          </Button>
        )}
        {/* Deliberately quiet — dismissing is for a pairing that has been
            checked and judged to be two genuine separate sales. */}
        <Button
          size="sm"
          variant="ghost"
          className="text-white/40 hover:text-white/70 hover:bg-white/5"
          disabled={working('dismiss', finding.id)}
          onClick={() =>
            onRepair('dismiss', finding.id, {
              pair_key: finding.id,
              ebay_order_id: ebay.id,
              other_order_id: other.id,
              summary: `${ebay.customer_name || '—'} ${money(ebay.amount)} · ${day(ebay.date)} ↔ ${
                other.customer_name || '—'
              } ${money(other.amount)} · ${day(other.date)}`,
            })
          }
        >
          {working('dismiss', finding.id) ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Ban className="w-3.5 h-3.5" />
          )}
          Dismiss — not a duplicate
        </Button>
      </div>

      {other.posted && ebay.posted && (
        <p className="text-xs text-amber-400 mt-3 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" /> Both copies are posted to Sage — the duplicate has
          to be voided or journalled out in Sage, not here.
        </p>
      )}

      {editing && (
        <div className="mt-3 border border-white/10 p-3 space-y-2">
          <p className="text-[10px] font-bold tracking-widest text-white/40">
            CORRECT THE BUYER ON THE OTHER RECORD — MONEY IS NOT TOUCHED
          </p>
          <input
            className="seo-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Buyer name"
          />
          <input
            className="seo-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Buyer email"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              className="bg-primary hover:bg-primary/90"
              disabled={working('correct_customer', other.id)}
              onClick={() =>
                onRepair('correct_customer', other.id, {
                  customer_name: name,
                  customer_email: email,
                }).then(() => setEditing(false))
              }
            >
              {working('correct_customer', other.id) && (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              )}
              Save details
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="bg-transparent border-white/15 text-white/70 hover:bg-white/5"
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}